import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  Autocomplete,
  Card,
  Alert,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Close as CloseIcon, 
  Add as AddIcon, 
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Photo as PhotoIcon
} from '@mui/icons-material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import citiesData, { City, findCityByName, searchCities } from '../../data/citiesData';
import { PlaceResult, getAccommodationsByCity, searchAccommodations } from '../../data/mockAccommodations';
import { enhanceTripDescription } from '../../services/tripAIService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import AIImageGenerator from '../../components/EventForms/AIImageGenerator';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import GooglePlacesSearch from '../common/GooglePlacesSearch';
// Import action tracking utilities
import { 
   trackUserAction, 
   trackClick, 
   trackQuickError,
   ACTION_TYPES, 
   ACTION_CATEGORIES,
   createTripMetadata,
   createPerformanceMetadata
} from '../../utils/actionTracker';

// Add Quill modules configuration
const quillModules = {
   toolbar: [
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote'],
      [{ color: [] }],
      ['link'],
      ['clean'],
   ],
   clipboard: {
      matchVisual: false,
   },
   keyboard: {
      bindings: {
         tab: false,
      },
   },
};

const quillFormats = [
   'header',
   'bold',
   'italic',
   'underline',
   'list',
   'bullet',
   'blockquote',
   'color',
   'link',
];

// Add custom styles for the editor
const editorStyles = {
   editor: {
      '.ql-container': {
         borderRadius: '0 0 4px 4px',
         backgroundColor: '#ffffff',
         fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
         fontSize: '1rem',
         lineHeight: '1.6',
      },
      '.ql-toolbar': {
         borderRadius: '4px 4px 0 0',
         backgroundColor: '#f5f5f5',
         borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '.ql-editor': {
         minHeight: '200px',
         maxHeight: '400px',
         overflowY: 'auto',
         padding: '16px',
         '&:focus': {
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
         },
         p: {
            marginBottom: '0.8em',
         },
         h2: {
            fontSize: '1.5rem',
            marginBottom: '1rem',
            fontWeight: 600,
         },
         h3: {
            fontSize: '1.25rem',
            marginBottom: '0.8rem',
            fontWeight: 500,
         },
         'ul, ol': {
            paddingLeft: '1.5em',
            marginBottom: '1em',
         },
         li: {
            marginBottom: '0.5em',
         },
         blockquote: {
            borderLeft: '4px solid #ccc',
            paddingLeft: '16px',
            marginLeft: '0',
            marginRight: '0',
            fontStyle: 'italic',
         },
      },
      '.ql-snow.ql-toolbar button:hover, .ql-snow .ql-toolbar button:hover': {
         color: '#1976d2',
      },
      '.ql-snow.ql-toolbar button.ql-active, .ql-snow .ql-toolbar button.ql-active': {
         color: '#1976d2',
      },
   },
};

interface Accommodation {
   name: string;
   address?: string;
   checkIn: Date | null;
   checkOut: Date | null;
   placeId?: string;
   coordinates?: [number, number];
}

interface HistoricalTripFormProps {
   open: boolean;
   onClose: () => void;
   onSuccess?: (tripId: string) => void;
}

const HistoricalTripForm: React.FC<HistoricalTripFormProps> = ({ open, onClose, onSuccess }) => {
   const { currentUser } = useUser();
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [saveSuccess, setSaveSuccess] = useState(false);
   const [enhancing, setEnhancing] = useState(false);
   const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
   const [suggestedHighlights, setSuggestedHighlights] = useState<string[]>([]);
   const [description, setDescription] = useState('');

   // Form state
   const [tripTitle, setTripTitle] = useState('');
   const [destination, setDestination] = useState('');
   const [destinationCoordinates, setDestinationCoordinates] = useState<[number, number] | null>(
      null
   );
   const [startDate, setStartDate] = useState<Date | null>(null);
   const [endDate, setEndDate] = useState<Date | null>(null);
   const [accommodations, setAccommodations] = useState<Accommodation[]>([
      { name: '', address: '', checkIn: null, checkOut: null },
   ]);

   // City and accommodation suggestions
   const [citySuggestions, setCitySuggestions] = useState<City[]>([]);
   const [accommodationSuggestions, setAccommodationSuggestions] = useState<
      Record<number, PlaceResult[]>
   >({});

   // AI generator state
   const [showAIGenerator, setShowAIGenerator] = useState(false);

   // Add new state variables
   const [coverPhoto, setCoverPhoto] = useState<File | null>(null);
   const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);
   const fileInputRef = useRef<HTMLInputElement>(null);

   // Update destination coordinates when a city is selected
   // useEffect(() => {
   //   if (destination) {
   //     const city = findCityByName(destination);
   //     if (city) {
   //       setDestinationCoordinates(city.coordinates);

   //       // Load accommodations for this city
   //       const cityAccommodations = getAccommodationsByCity(city.name);
   //       if (cityAccommodations.length > 0) {
   //         setAccommodationSuggestions({ 0: cityAccommodations });
   //       }
   //     }
   //   }
   // }, [destination]);

   // Update accommodation dates when trip dates change
   useEffect(() => {
      if (startDate || endDate) {
         const updatedAccommodations = accommodations.map((acc) => ({
            ...acc,
            checkIn: acc.checkIn || startDate,
            checkOut: acc.checkOut || endDate,
         }));
         setAccommodations(updatedAccommodations);
      }
   }, [startDate, endDate]);

   // const handleCityInputChange = (_event: React.SyntheticEvent, value: string) => {
   //   if (value) {
   //     const matchingCities = searchCities(value);
   //     setCitySuggestions(matchingCities);
   //   } else {
   //     setCitySuggestions([]);
   //   }
   // };

   // const handleCityChange = (_event: React.SyntheticEvent, value: string | null) => {
   //   if (value) {
   //     setDestination(value);
   //   }
   // };

   const handleAccommodationInputChange = (index: number, value: string) => {
      if (value) {
         const matchingAccommodations = searchAccommodations(value);
         setAccommodationSuggestions({
            ...accommodationSuggestions,
            [index]: matchingAccommodations,
         });
      } else {
         const newSuggestions = { ...accommodationSuggestions };
         delete newSuggestions[index];
         setAccommodationSuggestions(newSuggestions);
      }
   };

   const handleAccommodationSelection = (index: number, value: PlaceResult | null) => {
      if (value) {
         const newAccommodations = [...accommodations];
         newAccommodations[index] = {
            ...newAccommodations[index],
            // name: value.name,
            address: value.formatted_address,
            // placeId: value.place_id,
            coordinates: [value.geometry.location.lng, value.geometry.location.lat],
         };
         setAccommodations(newAccommodations);
      }
   };

   const handleAddAccommodation = () => {
      setAccommodations([
         ...accommodations,
         {
            name: '',
            address: '',
            checkIn: startDate, // Default to trip start date
            checkOut: endDate, // Default to trip end date
         },
      ]);
   };

   const handleRemoveAccommodation = (index: number) => {
      const newAccommodations = [...accommodations];
      newAccommodations.splice(index, 1);
      setAccommodations(newAccommodations);
   };

   const handleAccommodationChange = (index: number, field: keyof Accommodation, value: any) => {
      const newAccommodations = [...accommodations];
      newAccommodations[index] = { ...newAccommodations[index], [field]: value };
      setAccommodations(newAccommodations);
   };

   const validateForm = () => {
      if (!tripTitle.trim()) {
         setError('Please enter a trip title');
         return false;
      }
      if (!destination.trim()) {
         setError('Please enter a destination');
         return false;
      }
      if (!startDate) {
         setError('Please select a start date');
         return false;
      }
      if (!endDate) {
         setError('Please select an end date');
         return false;
      }
      if (startDate > endDate) {
         setError('End date cannot be before start date');
         return false;
      }
      return true;
   };

   const handleSubmit = async () => {
      if (!validateForm()) return;
      if (!currentUser) {
         setError('You must be logged in to create a trip');
         return;
      }

      // Track trip creation start
      const creationStartTime = Date.now();
      const tripMetadata = {
         tripTitle,
         destination,
         hasCoverPhoto: !!coverPhoto,
         hasDescription: !!description.trim(),
         accommodationCount: accommodations.filter((acc) => acc.name.trim() !== '').length,
         tripDuration:
            startDate && endDate
               ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
               : 0,
         hasDestinationCoordinates: !!destinationCoordinates,
         isHistorical: true,
         startTime: creationStartTime,
      };

      try {
         await trackUserAction({
            actionType: ACTION_TYPES.CREATE_TRIP,
            category: ACTION_CATEGORIES.TRIP,
            description: `Started creating historical trip: ${tripTitle}`,
            metadata: createTripMetadata('trip_creation_start', undefined, tripTitle, {
               source: 'historical_trip_form',
               context: 'trip_creation',
               tripDuration:
                  startDate && endDate
                     ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
                     : 0,
               accommodationCount: accommodations.filter((acc) => acc.name.trim() !== '').length,
               contentLength: description.trim().length,
               fileSize: coverPhoto?.size,
               destination,
               hasDestinationCoordinates: !!destinationCoordinates,
               isHistorical: true,
               startTime: creationStartTime,
            }),
         });
      } catch (trackingError) {
         console.warn('Failed to track trip creation start:', trackingError);
      }

      setLoading(true);
      setError(null);
      setSaveSuccess(false);

      try {
         // Filter out empty accommodations
         const validAccommodations = accommodations.filter((acc) => acc.name.trim() !== '');

         // Handle cover photo upload to Firebase Storage if it exists
         let coverPhotoURL = null;
         if (coverPhoto) {
            try {
               // Create a storage reference
               const storage = getStorage();
               const storageRef = ref(
                  storage,
                  `trips/${currentUser.uid}/covers/${Date.now()}_${coverPhoto.name}`
               );

               // Upload the file
               await uploadBytes(storageRef, coverPhoto);

               // Get the download URL
               coverPhotoURL = await getDownloadURL(storageRef);
            } catch (uploadError) {
               console.error('Error uploading cover photo:', uploadError);
               // Continue without the cover photo if upload fails
            }
         }

         // Create trip document without the File object
         const tripData = {
            title: tripTitle,
            description: cleanHtmlContent(description),
            primaryDestination: {
               name: destination,
               coordinates: destinationCoordinates,
            },
            startDate,
            endDate,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            owner: currentUser.uid,
            isHistorical: true,
            accommodations: validAccommodations.map((acc) => ({
               ...acc,
               name: acc.name,
               address: acc.address,
               checkIn: acc.checkIn,
               checkOut: acc.checkOut,
               //  placeId: acc.placeId,
               coordinates: acc.coordinates,
            })),
            // Don't include the File object directly
            coverImageUrl: coverPhotoURL, // Use the URL instead of the File object
            hasCoverPhoto: !!coverPhotoURL,
         };

         // Use backend API to create trip with proper members array
         const { createApiUrl } = await import('../../utils/apiConfig');
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();
         const apiUrl = createApiUrl('/api/trips');

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: tripTitle,
               description: cleanHtmlContent(description),
               startDate: startDate?.toISOString(),
               endDate: endDate?.toISOString(),
               isPublic: false,
               primaryDestination: destinationCoordinates
                  ? {
                       name: destination,
                       coordinates: destinationCoordinates,
                       country: destination, // You might want to extract country separately
                    }
                  : undefined,
               coverPhotoUrl: coverPhotoURL,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create trip');
         }

         const tripRef = { id: result.tripId };

         // Track successful trip creation
         const creationEndTime = Date.now();
         const creationDuration = (creationEndTime - creationStartTime) / 1000;

         try {
            await trackUserAction({
               actionType: ACTION_TYPES.CREATE_TRIP,
               category: ACTION_CATEGORIES.TRIP,
               description: `Successfully created historical trip: ${tripTitle}`,
               targetId: tripRef.id,
               targetType: 'trip',
               value: 1,
               metadata: createPerformanceMetadata('trip_creation_success', creationStartTime, {
                  source: 'historical_trip_form',
                  context: 'trip_creation',
                  targetId: tripRef.id,
                  targetTitle: tripTitle,
                  tripDuration:
                     startDate && endDate
                        ? Math.ceil(
                             (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
                          )
                        : 0,
                  accommodationCount: validAccommodations.length,
                  contentLength: cleanHtmlContent(description).length,
                  fileSize: coverPhoto?.size,
                  fileCount: coverPhotoURL ? 1 : 0,
                  destination,
                  hasDestinationCoordinates: !!destinationCoordinates,
                  isHistorical: true,
                  coverPhotoUploaded: !!coverPhotoURL,
                  destinationCoordinates: destinationCoordinates
                     ? {
                          lat: destinationCoordinates[0],
                          lng: destinationCoordinates[1],
                       }
                     : null,
               }),
            });
         } catch (trackingError) {
            console.warn('Failed to track trip creation success:', trackingError);
         }

         setSaveSuccess(true);

         // Reset form
         setTripTitle('');
         setDestination('');
         setDestinationCoordinates(null);
         setStartDate(null);
         setEndDate(null);
         setAccommodations([{ name: '', address: '', checkIn: null, checkOut: null }]);

         // Close dialog and trigger success callback
         if (onSuccess) {
            onSuccess(tripRef.id);
         }

         // Short delay to show success message
         setTimeout(() => {
            onClose();
         }, 1000);
      } catch (err) {
         console.error('Error creating historical trip:', err);
         setError('Failed to create trip. Please try again.');

         // Track trip creation error
         try {
            await trackQuickError(
               err instanceof Error ? err : new Error('Trip creation failed'),
               'trip_creation',
               {
                  ...tripMetadata,
                  action: 'trip_creation_error',
                  creationDuration: (Date.now() - creationStartTime) / 1000,
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track trip creation error:', trackingError);
         }
      } finally {
         setLoading(false);
      }
   };

   const handleEnhanceWithAI = async () => {
      if (!tripTitle) {
         setError('Please enter a trip title first');
         return;
      }

      if (!destination) {
         setError('Please enter a destination first');
         return;
      }

      // Track AI enhancement start
      const enhancementStartTime = Date.now();
      const aiMetadata = {
         tripTitle,
         destination,
         hasExistingDescription: !!description.trim(),
         existingDescriptionLength: description.trim().length,
         hasStartDate: !!startDate,
         hasEndDate: !!endDate,
         startTime: enhancementStartTime,
      };

      try {
         await trackUserAction({
            actionType: 'ai_enhance_trip',
            category: ACTION_CATEGORIES.TRIP,
            description: `Started AI enhancement for trip: ${tripTitle}`,
            metadata: createTripMetadata('ai_enhancement_start', undefined, tripTitle, {
               source: 'historical_trip_form',
               context: 'trip_creation',
               contentLength: description.trim().length,
               destination,
               hasExistingDescription: !!description.trim(),
               hasStartDate: !!startDate,
               hasEndDate: !!endDate,
               startTime: enhancementStartTime,
            }),
         });
      } catch (trackingError) {
         console.warn('Failed to track AI enhancement start:', trackingError);
      }

      try {
         setEnhancing(true);
         setError(null);

         const enhancement = await enhanceTripDescription({
            title: tripTitle,
            description: description || undefined,
            startDate: startDate || new Date(),
            endDate: endDate || new Date(),
            destination: {
               placeId: '',
               description: destination,
               mainText: destination,
               secondaryText: '',
               coordinates: destinationCoordinates
                  ? {
                       lat: destinationCoordinates[0],
                       lng: destinationCoordinates[1],
                    }
                  : {
                       lat: 0,
                       lng: 0,
                    },
            },
         });

         // Update form with enhanced content
         if (enhancement.suggestedTitle) {
            setTripTitle(enhancement.suggestedTitle);
         }

         // Clean the enhanced description - remove hashtags and convert to proper HTML
         let cleanedDescription = enhancement.enhancedDescription;

         // Replace markdown-style headers with proper HTML headers
         cleanedDescription = cleanedDescription.replace(/# (.*?)(\n|$)/g, '<h2>$1</h2>');
         cleanedDescription = cleanedDescription.replace(/## (.*?)(\n|$)/g, '<h3>$1</h3>');

         // Convert bullet points to proper HTML lists
         const bulletPointsRegex = /- (.*?)(\n|$)/g;
         if (bulletPointsRegex.test(cleanedDescription)) {
            // First identify all bullet point sections
            const sections = cleanedDescription.split(/\n\n+/);

            cleanedDescription = sections
               .map((section) => {
                  if (section.trim().startsWith('- ')) {
                     // This is a bullet list section, convert it
                     const listItems = section
                        .split(/\n/)
                        .filter((line) => line.trim().startsWith('- '));
                     const formattedItems = listItems
                        .map((item) => `<li>${item.replace(/- /, '')}</li>`)
                        .join('');
                     return `<ul>${formattedItems}</ul>`;
                  }
                  return section;
               })
               .join('\n\n');
         }

         // Set the cleaned description
         setDescription(cleanedDescription);

         // Update suggested tags and highlights
         setSuggestedTags(enhancement.suggestedTags || []);
         setSuggestedHighlights(enhancement.suggestedHighlights || []);

         // Track successful AI enhancement
         const enhancementEndTime = Date.now();
         const enhancementDuration = (enhancementEndTime - enhancementStartTime) / 1000;

         try {
            await trackUserAction({
               actionType: 'ai_enhance_trip',
               category: ACTION_CATEGORIES.TRIP,
               description: `Successfully enhanced trip with AI: ${tripTitle}`,
               metadata: createPerformanceMetadata('ai_enhancement_success', enhancementStartTime, {
                  source: 'historical_trip_form',
                  context: 'trip_creation',
                  targetTitle: tripTitle,
                  contentLength: cleanedDescription.length,
                  destination,
                  titleChanged: enhancement.suggestedTitle !== tripTitle,
                  features: ['ai_enhancement', 'description_generation'],
                  settings: {
                     enhancedTagsCount: enhancement.suggestedTags?.length || 0,
                     enhancedHighlightsCount: enhancement.suggestedHighlights?.length || 0,
                  },
               }),
            });
         } catch (trackingError) {
            console.warn('Failed to track AI enhancement success:', trackingError);
         }

         // Show success message
         setError(null);
      } catch (err) {
         console.error('Error enhancing trip:', err);
         setError('Failed to enhance trip details. Please try again.');

         // Track AI enhancement error
         try {
            await trackQuickError(
               err instanceof Error ? err : new Error('AI enhancement failed'),
               'ai_trip_enhancement',
               {
                  ...aiMetadata,
                  action: 'ai_enhancement_error',
                  enhancementDuration: (Date.now() - enhancementStartTime) / 1000,
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track AI enhancement error:', trackingError);
         }
      } finally {
         setEnhancing(false);
      }
   };

   const cleanHtmlContent = (html: string): string => {
      if (!html) return '';

      // Clean the HTML using DOMPurify with allowed tags and attributes
      let cleaned = DOMPurify.sanitize(html, {
         ALLOWED_TAGS: [
            'p',
            'br',
            'strong',
            'em',
            'u',
            'h1',
            'h2',
            'h3',
            'ul',
            'ol',
            'li',
            'blockquote',
            'a',
            'span',
         ],
         ALLOWED_ATTR: ['href', 'style'],
      });

      // Remove empty paragraphs that only contain &nbsp; or whitespace
      cleaned = cleaned.replace(/<p[^>]*>(\s|&nbsp;)*<\/p>/g, '');

      // Remove excessive line breaks while preserving intentional ones
      cleaned = cleaned.replace(/(\r\n|\n|\r){3,}/g, '\n\n');

      // Remove any style attributes that might have been added by the editor
      cleaned = cleaned.replace(/\s+style="[^"]*"/g, '');

      // Fix any double spaces
      cleaned = cleaned.replace(/\s{2,}/g, ' ');

      return cleaned.trim();
   };

   const handleFileSelect = () => {
      fileInputRef.current?.click();
   };

   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
         const file = files[0];
         setCoverPhoto(file);
         const reader = new FileReader();
         reader.onloadend = () => {
            setCoverPhotoUrl(reader.result as string);
         };
         reader.readAsDataURL(file);
      }
   };

   const handleAIImageGenerated = (imageFile: File | null, previewUrl: string | null) => {
      setCoverPhoto(imageFile);
      setCoverPhotoUrl(previewUrl);
      setShowAIGenerator(false);
   };

   return (
      <Dialog
         open={open}
         onClose={onClose}
         fullWidth
         maxWidth='md'
         PaperProps={{
            sx: {
               borderRadius: 2,
               maxHeight: '90vh',
            },
         }}
      >
         <DialogTitle>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
               <Typography variant='h6'>Add Historical Trip</Typography>
               <IconButton onClick={onClose} size='small'>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>
         <DialogContent dividers>
            {/* AI Enhancement Card */}
            <Card
               sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  borderRadius: 2,
                  boxShadow: 1,
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 28 }} />
                  <Typography variant='h6'>Enhance Your Trip Details with AI</Typography>
               </Box>
               <Typography variant='body2' sx={{ mb: 2 }}>
                  Enter your trip title and destination, then let AI help you create a detailed trip
                  record. The AI will generate a structured description with practical information
                  based on your destination.
               </Typography>
               <Button
                  variant='contained'
                  onClick={handleEnhanceWithAI}
                  disabled={enhancing || !tripTitle || !destination}
                  startIcon={enhancing ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                  sx={{ bgcolor: 'background.paper', color: 'primary.main' }}
               >
                  {enhancing ? 'Enhancing Trip Details...' : 'Enhance Trip Details'}
               </Button>
            </Card>

            {/* Cover Photo Display Section */}
            <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
               <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                  Cover Photo
               </Typography>
               <Box sx={{ mb: 2 }}>
                  {coverPhotoUrl ? (
                     <Box sx={{ position: 'relative' }}>
                        <img
                           src={coverPhotoUrl}
                           alt='Trip Cover'
                           style={{
                              width: '100%',
                              height: '240px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                           }}
                        />
                        <Button
                           variant='contained'
                           color='error'
                           size='small'
                           sx={{ position: 'absolute', top: 10, right: 10 }}
                           onClick={() => {
                              setCoverPhoto(null);
                              setCoverPhotoUrl(null);
                           }}
                        >
                           Remove
                        </Button>
                     </Box>
                  ) : (
                     <Box
                        sx={{
                           width: '100%',
                           height: '160px',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           backgroundColor: 'grey.100',
                           borderRadius: '8px',
                           border: '1px dashed grey.400',
                        }}
                     >
                        <Typography color='text.secondary'>No cover photo selected</Typography>
                     </Box>
                  )}
               </Box>

               <Grid container spacing={1}>
                  <Grid item xs={6}>
                     <Button
                        startIcon={<PhotoIcon />}
                        variant='outlined'
                        fullWidth
                        onClick={handleFileSelect}
                     >
                        Upload Photo
                     </Button>
                  </Grid>
                  <Grid item xs={6}>
                     <Button
                        startIcon={<AutoAwesomeIcon />}
                        variant='outlined'
                        color='secondary'
                        fullWidth
                        onClick={() => setShowAIGenerator(true)}
                     >
                        Generate with AI
                     </Button>
                  </Grid>
               </Grid>
            </Paper>

            {/* Basic trip information section */}
            <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
               <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                  Trip Basics
               </Typography>
               <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                     <TextField
                        label='Trip Title'
                        fullWidth
                        required
                        value={tripTitle}
                        onChange={(e) => setTripTitle(e.target.value)}
                        margin='normal'
                        variant='outlined'
                        placeholder='e.g., Weekend in Paris'
                     />
                  </Grid>
                  <Grid item xs={12} md={6}>
                     <GooglePlacesSearch
                        //  sx={{ mb: 2 }}
                        value={destination || ''}
                        searchType='places'
                        label='Destination'
                        fullWidth
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           setDestination(place_name);
                           setDestinationCoordinates([lat, lng]);
                        }}
                     />
                  </Grid>
               </Grid>

               <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={3}>
                     <Grid item xs={12} md={6}>
                        <DatePicker
                           label='Start Date'
                           value={startDate}
                           onChange={(date) => setStartDate(date as Date | null)}
                           renderInput={(params) => (
                              <TextField {...params} fullWidth margin='normal' required />
                           )}
                        />
                     </Grid>
                     <Grid item xs={12} md={6}>
                        <DatePicker
                           label='End Date'
                           value={endDate}
                           onChange={(date) => setEndDate(date as Date | null)}
                           renderInput={(params) => (
                              <TextField {...params} fullWidth margin='normal' required />
                           )}
                        />
                     </Grid>
                  </Grid>
               </LocalizationProvider>
            </Paper>

            {/* Description Editor */}
            <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
               <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                  Trip Description
               </Typography>
               <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
                  Share your experiences, highlights, and memorable moments from this trip.
               </Typography>
               <Paper variant='outlined' sx={{ ...editorStyles.editor }}>
                  <ReactQuill
                     value={description}
                     onChange={setDescription}
                     modules={quillModules}
                     formats={quillFormats}
                     placeholder='Start writing your trip story...'
                  />
               </Paper>
               <Typography variant='caption' color='textSecondary' sx={{ mt: 1, display: 'block' }}>
                  Tip: Use headings to organize your content, and add lists for key highlights or
                  activities.
               </Typography>
            </Paper>

            {/* Accommodations section */}
            <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
               <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
                  <Typography variant='subtitle1' fontWeight={600}>
                     Accommodations
                  </Typography>
                  <Button
                     startIcon={<AddIcon />}
                     onClick={handleAddAccommodation}
                     variant='outlined'
                     size='small'
                  >
                     Add Accommodation
                  </Button>
               </Box>
               <Typography variant='body2' color='textSecondary' gutterBottom>
                  Add where you stayed during this trip
               </Typography>

               {accommodations.map((accommodation, index) => (
                  <Paper key={index} variant='outlined' sx={{ p: 2, mb: 2, borderRadius: 1 }}>
                     <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                        <Typography variant='subtitle2'>Accommodation {index + 1}</Typography>
                        {accommodations.length > 1 && (
                           <IconButton
                              size='small'
                              onClick={() => handleRemoveAccommodation(index)}
                              color='error'
                           >
                              <CloseIcon />
                           </IconButton>
                        )}
                     </Box>

                     <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                           <TextField
                              label='Name'
                              fullWidth
                              value={accommodation.name}
                              onChange={(e) =>
                                 handleAccommodationChange(index, 'name', e.target.value)
                              }
                              margin='normal'
                              placeholder='Accommodation Name'
                           />
                           {/* <Autocomplete
                              freeSolo
                              options={accommodationSuggestions[index] || []}
                              getOptionLabel={(option) =>
                                 typeof option === 'string' ? option : option.name
                              }
                              onInputChange={(event, value) => {
                                 handleAccommodationInputChange(index, value);
                                 handleAccommodationChange(index, 'name', value);
                              }}
                              onChange={(event, value) =>
                                 handleAccommodationSelection(index, value as PlaceResult | null)
                              }
                              renderInput={(params) => (
                                 <TextField
                                    {...params}
                                    label='Accommodation Name'
                                    fullWidth
                                    margin='normal'
                                    value={accommodation.name}
                                    placeholder='e.g., Marriott Hotel'
                                 />
                              )}
                           /> */}
                        </Grid>
                        <Grid item xs={12} md={6}>
                           <GooglePlacesSearch
                              //  sx={{ mb: 2 }}
                              value={accommodation.address || ''}
                              searchType='places'
                              label='Address'
                              fullWidth
                              onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                                 handleAccommodationSelection(index, {
                                    name: place_name,
                                    formatted_address: place_name,
                                    geometry: {
                                       location: {
                                          lat: lat,
                                          lng: lng,
                                       },
                                    },
                                 });
                              }}
                           />
                        </Grid>
                     </Grid>

                     <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2}>
                           <Grid item xs={12} md={6}>
                              <DatePicker
                                 label='Check-in Date'
                                 value={accommodation.checkIn}
                                 onChange={(date) =>
                                    handleAccommodationChange(index, 'checkIn', date as Date | null)
                                 }
                                 renderInput={(params) => (
                                    <TextField {...params} fullWidth margin='normal' />
                                 )}
                              />
                           </Grid>
                           <Grid item xs={12} md={6}>
                              <DatePicker
                                 label='Check-out Date'
                                 value={accommodation.checkOut}
                                 onChange={(date) =>
                                    handleAccommodationChange(
                                       index,
                                       'checkOut',
                                       date as Date | null
                                    )
                                 }
                                 renderInput={(params) => (
                                    <TextField {...params} fullWidth margin='normal' />
                                 )}
                              />
                           </Grid>
                        </Grid>
                     </LocalizationProvider>
                  </Paper>
               ))}
            </Paper>

            {/* Display AI suggestions section */}
            {(suggestedHighlights.length > 0 || suggestedTags.length > 0) && (
               <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                  <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                     AI Suggestions
                  </Typography>

                  {/* Suggested highlights */}
                  {suggestedHighlights.length > 0 && (
                     <Box sx={{ mb: 2 }}>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           color='primary.main'
                           sx={{ fontWeight: 'bold' }}
                        >
                           Suggested Highlights
                        </Typography>
                        <Box sx={{ bgcolor: 'success.light', borderRadius: 1, p: 2 }}>
                           <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                              {suggestedHighlights.map((highlight, index) => (
                                 <li key={index} style={{ marginBottom: '0.5rem' }}>
                                    {highlight}
                                 </li>
                              ))}
                           </ul>
                        </Box>
                     </Box>
                  )}

                  {/* Suggested tags */}
                  {suggestedTags.length > 0 && (
                     <Box>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           color='primary.main'
                           sx={{ fontWeight: 'bold' }}
                        >
                           Suggested Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                           {suggestedTags.map((tag, index) => (
                              <Chip
                                 key={index}
                                 label={tag}
                                 size='small'
                                 color='primary'
                                 variant='outlined'
                              />
                           ))}
                        </Box>
                     </Box>
                  )}
               </Paper>
            )}

            {/* Error and success messages */}
            {error && (
               <Alert severity='error' sx={{ mt: 2, mb: 2 }}>
                  {error}
               </Alert>
            )}

            {saveSuccess && (
               <Alert severity='success' sx={{ mt: 2, mb: 2 }}>
                  Trip successfully saved!
               </Alert>
            )}

            <Grid container spacing={1}>
               <Grid item xs={6}>
                  <Button
                     startIcon={<PhotoIcon />}
                     variant='outlined'
                     fullWidth
                     onClick={handleFileSelect}
                  >
                     Upload Photo
                  </Button>
               </Grid>
               <Grid item xs={6}>
                  <Button
                     startIcon={<AutoAwesomeIcon />}
                     variant='outlined'
                     color='secondary'
                     fullWidth
                     onClick={() => setShowAIGenerator(true)}
                  >
                     Generate with AI
                  </Button>
               </Grid>
            </Grid>

            {/* Hidden file input */}
            <input
               type='file'
               accept='image/*'
               hidden
               ref={fileInputRef}
               onChange={handleFileChange}
            />

            {/* AI Image Generator */}
            <AIImageGenerator
               open={showAIGenerator}
               onClose={() => setShowAIGenerator(false)}
               eventTitle={tripTitle || 'Trip'}
               eventDescription={description || ''}
               eventType={'travel'}
               onImageGenerated={handleAIImageGenerated}
            />
         </DialogContent>
         <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} disabled={loading}>
               Cancel
            </Button>
            <Button
               onClick={handleSubmit}
               variant='contained'
               color='primary'
               disabled={loading}
               startIcon={loading ? <CircularProgress size={20} /> : null}
            >
               {loading ? 'Saving Trip...' : 'Add Historical Trip'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default HistoricalTripForm; 